import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Hero = () => {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "",
    property_location: "", development_type: "", project_stage: "", assistance: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quick-contact`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        property_location: formData.property_location,
        development_type: formData.development_type,
        assistance: formData.assistance,
      });
      toast.success("Thank you! We'll be in touch about your development.");
      setFormData({ name: "", email: "", phone: "", property_location: "", development_type: "", project_stage: "", assistance: "" });
      setShowForm(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1570426373775-9f25c0d43cce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBsYXclMjBvZmZpY2UlMjBjb3Jwb3JhdGUlMjBidWlsZGluZyUyMG1vZGVybnxlbnwwfHx8fDE3NzQwMjU5MDd8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="hero-section"
    >
      <div className="absolute inset-0 hero-overlay" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — Text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Logo */}
            <motion.img
              src="https://customer-assets.emergentagent.com/job_mheshe-legal/artifacts/1xa23sin_Mheshe%20Attorneys.png"
              alt="Mheshe Attorneys Inc"
              className="h-16 sm:h-24 lg:h-32 w-auto mb-6 sm:mb-8 drop-shadow-lg"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              data-testid="hero-logo"
            />
            <p className="uppercase tracking-[0.2em] text-xs font-bold text-brand-gold mb-6">
              Chosen To Serve!
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl font-semibold text-white leading-none tracking-tighter mb-6">
              Property Led. Strategically Focused.
            </h1>
            <p className="text-xl sm:text-2xl text-white/80 font-sans font-light mb-4">
              Attorneys | Conveyancers | Notaries
            </p>
            <p className="text-base sm:text-lg text-white/60 font-sans mb-3 max-w-lg">
              Mheshe Attorneys is a specialist, 100% black woman-owned legal practice providing integrated legal support across the property development lifecycle.
            </p>
            <p className="text-sm sm:text-base text-white/50 font-sans mb-8 sm:mb-10 max-w-lg">
              From the acquisition and structuring of land to the opening of township registers, registration of property rights and transfer to end purchasers, we combine specialist conveyancing and notarial expertise with commercial, litigation and governance insight.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={() => setShowForm(true)}
                className="btn-gold px-6 sm:px-8 py-4 text-sm sm:text-base font-semibold uppercase tracking-wider rounded-none"
                data-testid="hero-discuss-development-btn"
              >
                Discuss Your Development
              </Button>
              <button
                onClick={() => scrollToSection("#services")}
                className="text-brand-gold hover:text-white border-b border-brand-gold/50 hover:border-white pb-1 text-sm font-semibold uppercase tracking-wider transition-colors"
                data-testid="hero-explore-btn"
              >
                Explore Our Expertise
              </button>
            </div>
          </motion.div>

          {/* Right — Form (inline like current hero) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="glass-card p-8 lg:p-10"
          >
            {!showForm ? (
              <>
                <h3 className="font-serif text-2xl text-white mb-2">
                  Discuss Your Development
                </h3>
                <p className="text-white/60 text-sm mb-8">
                  Tell us about your property development and we'll connect you with the right expertise.
                </p>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <Input
                    type="text" name="name" placeholder="Your Name" required
                    value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none"
                    data-testid="hero-form-name"
                  />
                  <Input
                    type="email" name="email" placeholder="Email Address" required
                    value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none"
                    data-testid="hero-form-email"
                  />
                  <Input
                    type="tel" name="phone" placeholder="Phone Number" required
                    value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none"
                    data-testid="hero-form-phone"
                  />
                  <Input
                    type="text" name="property_location" placeholder="Property Location"
                    value={formData.property_location} onChange={(e) => setFormData({...formData, property_location: e.target.value})}
                    className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none"
                    data-testid="hero-form-location"
                  />
                  <select
                    value={formData.development_type}
                    onChange={(e) => setFormData({...formData, development_type: e.target.value})}
                    className="w-full py-3 bg-transparent border-b border-white/20 text-white/80 text-sm focus:border-brand-gold outline-none [&>option]:bg-charcoal [&>option]:text-white"
                    data-testid="hero-form-dev-type"
                  >
                    <option value="">Development Type</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                    <option value="mixed-use">Mixed-Use</option>
                    <option value="sectional-title">Sectional Title</option>
                    <option value="township">Township Development</option>
                    <option value="other">Other</option>
                  </select>
                  <Button
                    type="submit" disabled={isSubmitting}
                    className="btn-gold w-full py-4 text-base font-semibold uppercase tracking-wider rounded-none"
                    data-testid="hero-form-submit-btn"
                  >
                    {isSubmitting ? "Sending..." : "Submit Enquiry"}
                  </Button>
                </form>
                <p className="text-white/40 text-xs mt-6 text-center">
                  Your information is secure and will never be shared.
                </p>
              </>
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="font-serif text-2xl text-white">Property Enquiry</h3>
                  <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white"><X className="h-5 w-5" /></button>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input type="text" placeholder="Your Name" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none" />
                  <Input type="email" placeholder="Email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none" />
                  <Input type="tel" placeholder="Phone" required value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none" />
                  <Input type="text" placeholder="Property Location" value={formData.property_location} onChange={(e) => setFormData({...formData, property_location: e.target.value})} className="form-input-underline w-full py-3 text-white placeholder:text-white/40 bg-transparent rounded-none" />
                  <Textarea placeholder="What assistance do you need?" value={formData.assistance} onChange={(e) => setFormData({...formData, assistance: e.target.value})} rows={3} className="w-full py-3 text-white placeholder:text-white/40 bg-transparent border-b border-white/20 rounded-none resize-none" />
                  <Button type="submit" disabled={isSubmitting} className="btn-gold w-full py-4 text-base font-semibold uppercase tracking-wider rounded-none">
                    {isSubmitting ? "Sending..." : "Submit Enquiry"}
                  </Button>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
